export interface DepartmentPayroll {
  departmentId: string;
  departmentName: string;
  ftes: number;
  annualCost: number;
  monthlyCost: number;
  positions: any[];
}

export interface PayrollTotals {
  ftes: number;
  annualCost: number;
  monthlyCost: number;
}

export interface ProcessedPayrollResult {
  departments: DepartmentPayroll[];
  totals: PayrollTotals;
  flat: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Excel sheet "Payroll Model" structure (confirmed from file):
//   Col A = role name
//   Col D = FTEs
//   Col E = annual salary per FTE
//   Col F = D*E (total salary, formula — do NOT write)
//   Col G = social charges (decimal, e.g. 0.25)
//   Col H = total cost incl. social charges (formula — do NOT write)
//
// Dept blocks — 7 role slots each:
//   rooms        header row 7,  roles rows  8–14  ← Front Office + Housekeeping MERGED
//   fnb          header row 17, roles rows 18–24
//   wellness     header row 27, roles rows 28–34
//   ag           header row 37, roles rows 38–44
//   sales        header row 47, roles rows 48–54
//   maintenance  header row 57, roles rows 58–64
//
// PL sheet payroll rows (6 lines, matches 6 Excel dept blocks):
//   Row 13: Rooms Direct Payroll
//   Row 16: F&B Direct Payroll
//   Row 21: Wellness Direct Payroll
//   Row 31: A&G Payroll
//   Row 36: Sales & Marketing Payroll
//   Row 39: Maintenance Payroll
// ---------------------------------------------------------------------------

// Maps DB department UUID → Excel block key
// NOTE: Both "Direct Rooms" (Front Office) and "Housekeeping" map to "rooms"
//       because the Excel model merges them into one 7-slot block.
const DEPT_KEY_MAP: Record<string, string> = {
  "0d04c044-87bd-4d24-9007-f0628ef6ee3a": "rooms", // Direct Rooms / Front Office
  "e156800b-55e8-4b3f-a5f9-64b8fe266512": "rooms", // Housekeeping → merged into rooms
  "d93b774f-3906-47e9-ba2f-90675588f31c": "fnb", // F&B
  "559c7da5-01f0-4a2d-9764-ad7d6fb70220": "wellness", // Wellness / Spa
  "508d5c3b-c493-43f9-a796-11558a7a1fc9": "ag", // Admin & General
  "8607eb3b-2103-4615-b96c-9eef9f282afa": "sales", // Sales & Marketing
  "c5e5738e-0b31-4940-9b10-8ca654b9917f": "maintenance", // Maintenance
};

// Human-readable names for the UI departments array
const DEPT_DISPLAY_NAMES: Record<string, string> = {
  rooms: "Direct Rooms Payroll",
  fnb: "F&B Direct Payroll",
  wellness: "Wellness Direct Payroll",
  ag: "A&G Payroll",
  sales: "Sales & Marketing Payroll",
  maintenance: "Maintenance Payroll",
};

// Strict order matches Excel block order (rooms→fnb→wellness→ag→sales→maintenance)
const DEPT_ORDER = [
  "rooms",
  "fnb",
  "wellness",
  "ag",
  "sales",
  "maintenance",
] as const;
type ExcelDeptKey = (typeof DEPT_ORDER)[number];

export function processPayrollData(payrollData: any): ProcessedPayrollResult {
  if (!payrollData?.data?.positions) {
    return {
      departments: [],
      totals: { ftes: 0, annualCost: 0, monthlyCost: 0 },
      flat: {},
    };
  }

  const positions = payrollData.data.positions;

  // socialChargesPercent = 25 → decimal for Excel = 0.25
  const socialChargesPercent: number =
    payrollData.data.socialChargesPercent ?? 0;
  const socialChargesDecimal: number = socialChargesPercent / 100;

  const flat: Record<string, any> = {};

  // Group included positions by excel dept key, in DB insertion order
  const deptPositions: Record<ExcelDeptKey, any[]> = {
    rooms: [],
    fnb: [],
    wellness: [],
    ag: [],
    sales: [],
    maintenance: [],
  };

  for (const pos of positions) {
    if (!pos.included) continue;
    const key = (DEPT_KEY_MAP[pos.departmentId] ?? "ag") as ExcelDeptKey;
    deptPositions[key].push(pos);
  }

  let totalFtes = 0;
  let totalAnnualCost = 0;
  let totalMonthlyCost = 0;

  // Iterate in strict Excel order
  for (const excelKey of DEPT_ORDER) {
    const posList = deptPositions[excelKey];
    let deptFtes = 0;
    let deptMonthlyCost = 0;

    const maxSlots = 7;

    for (let roleNum = 1; roleNum <= maxSlots; roleNum++) {
      const isLastSlot = roleNum === maxSlots;
      const hasOverflow = posList.length > maxSlots;

      if (!isLastSlot || !hasOverflow) {
        // Normal path: one position per slot (or empty)
        const pos = posList[roleNum - 1];

        if (pos) {
          const ftes = pos.overrideFTEs ?? pos.calculatedFTEs ?? 0;
          const monthlySalary = pos.overrideSalary ?? pos.monthlySalary ?? 0;
          const annualSalaryPerFte = monthlySalary * 12;
          const totalSalary = annualSalaryPerFte * ftes;
          const totalCost = totalSalary * (1 + socialChargesDecimal);

          flat[`kpis.payroll.${excelKey}.role${roleNum}.name`] =
            pos.positionName;
          flat[`kpis.payroll.${excelKey}.role${roleNum}.fte`] = ftes;
          flat[`kpis.payroll.${excelKey}.role${roleNum}.salaryPerFte`] =
            annualSalaryPerFte;
          flat[`kpis.payroll.${excelKey}.role${roleNum}.totalSalary`] =
            totalSalary;
          flat[`kpis.payroll.${excelKey}.role${roleNum}.socialCharges`] =
            socialChargesDecimal;
          flat[`kpis.payroll.${excelKey}.role${roleNum}.totalCost`] = totalCost;

          deptFtes += ftes;
          deptMonthlyCost += monthlySalary * ftes * (1 + socialChargesDecimal);
        } else {
          // Empty slot — write zeros so Excel doesn't keep stale data
          flat[`kpis.payroll.${excelKey}.role${roleNum}.name`] = "-";
          flat[`kpis.payroll.${excelKey}.role${roleNum}.fte`] = 0;
          flat[`kpis.payroll.${excelKey}.role${roleNum}.salaryPerFte`] = 0;
          flat[`kpis.payroll.${excelKey}.role${roleNum}.totalSalary`] = 0;
          flat[`kpis.payroll.${excelKey}.role${roleNum}.socialCharges`] =
            socialChargesDecimal;
          flat[`kpis.payroll.${excelKey}.role${roleNum}.totalCost`] = 0;
        }
      } else {
        // Last slot with overflow: aggregate positions index 6+ into one row
        const remaining = posList.slice(maxSlots - 1);
        let aggFtes = 0;
        let aggTotalSalary = 0;
        let aggTotalCost = 0;
        let aggMonthlyCost = 0;
        const names: string[] = [];

        for (const p of remaining) {
          const ftes = p.overrideFTEs ?? p.calculatedFTEs ?? 0;
          const monthlySalary = p.overrideSalary ?? p.monthlySalary ?? 0;
          const annualSalaryPerFte = monthlySalary * 12;
          const totalCost =
            annualSalaryPerFte * ftes * (1 + socialChargesDecimal);

          aggFtes += ftes;
          aggTotalSalary += annualSalaryPerFte * ftes;
          aggTotalCost += totalCost;
          aggMonthlyCost += monthlySalary * ftes * (1 + socialChargesDecimal);
          names.push(p.positionName);
        }

        const avgSalaryPerFte = aggFtes > 0 ? aggTotalSalary / aggFtes : 0;

        flat[`kpis.payroll.${excelKey}.role${roleNum}.name`] =
          names.length > 1
            ? `${names[0]} +${names.length - 1}`
            : (names[0] ?? "");
        flat[`kpis.payroll.${excelKey}.role${roleNum}.fte`] = aggFtes;
        flat[`kpis.payroll.${excelKey}.role${roleNum}.salaryPerFte`] =
          avgSalaryPerFte;
        flat[`kpis.payroll.${excelKey}.role${roleNum}.totalSalary`] =
          aggTotalSalary;
        flat[`kpis.payroll.${excelKey}.role${roleNum}.socialCharges`] =
          socialChargesDecimal;
        flat[`kpis.payroll.${excelKey}.role${roleNum}.totalCost`] =
          aggTotalCost;

        deptFtes += aggFtes;
        deptMonthlyCost += aggMonthlyCost;
      }
    }

    const deptAnnualCost = deptMonthlyCost * 12;
    flat[`kpis.payroll.${excelKey}.total.fte`] = deptFtes;
    flat[`kpis.payroll.${excelKey}.total.totalCost`] = deptAnnualCost;

    totalFtes += deptFtes;
    totalAnnualCost += deptAnnualCost;
    totalMonthlyCost += deptMonthlyCost;
  }

  // Top-level totals
  flat["kpis.payroll.totalFTEs"] = totalFtes;
  flat["kpis.payroll.totalAnnual"] = totalAnnualCost;
  flat["kpis.payroll.socialChargesPercent"] = socialChargesDecimal; // decimal for Excel

  // Departments array for UI — in Excel order, with proper display names
  const departments: DepartmentPayroll[] = DEPT_ORDER.map((excelKey) => {
    const annualCost =
      (flat[`kpis.payroll.${excelKey}.total.totalCost`] as number) ?? 0;
    const ftes = (flat[`kpis.payroll.${excelKey}.total.fte`] as number) ?? 0;
    return {
      departmentId: excelKey,
      departmentName: DEPT_DISPLAY_NAMES[excelKey],
      ftes,
      annualCost,
      monthlyCost: annualCost / 12,
      positions: deptPositions[excelKey],
    };
  });

  const totals: PayrollTotals = {
    ftes: totalFtes,
    annualCost: totalAnnualCost,
    monthlyCost: totalMonthlyCost,
  };

  return { departments, totals, flat };
}
